import React from 'react';
import { Breadcrumbs as MuiBreadcrumbs, Typography, Link, Box } from '@mui/material';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { NavigateNext } from '@mui/icons-material';

const Breadcrumbs: React.FC = () => {
    const location = useLocation();
    const pathnames = location.pathname.split('/').filter((x) => x);

    // Define route mapping for nicer labels
    const routeMapping: { [key: string]: string } = {
        'companies': 'Companies',
        'projects': 'Projects',
        'clients': 'Clients',
        'assistant': 'AI Assistant',
        'tasks': 'Tasks',
        'users': 'Users',
        'settings': 'Settings',
        'admin': 'Admin',
        'ai-interactions': 'AI Interaction Log',
        'ai-prompts': 'AI Prompt Console',
        'inventory': 'Inventory',
        'reports': 'Reports',
        'analytics': 'Analytics',
        'my-assignments': 'My Assignments',
        'project-templates': 'Project Templates',
        'departments': 'Departments',
        'audit-logs': 'Audit Logs',
        'integrations': 'Integrations',
        'tools': 'Tools',
        's3-browser': 'S3 Browser',
        'db-console': 'Database Console',
        'db-performance': 'Query Performance',
        'google-workspace': 'Google Workspace',
        'meraki-vpn': 'Meraki VPN',
        'knowledge-base': 'Knowledge Base',
        'documents': 'Documents',
        'lis': 'LIS',
        'orders': 'Orders',
        'billing': 'Billing',
    };

    const getBreadcrumbName = (value: string, index: number, pathParts: string[]) => {
        // If it's a UUID/ID (simple check: length > 20 or includes numbers and not in mapping)
        // we can use "Details" or try to contextually name it based on the parent
        if (value.match(/^[0-9a-fA-F-]{36}$/) || (value.length > 8 && /\d/.test(value))) {
            const parent = index > 0 ? pathParts[index - 1] : '';
            if (parent === 'companies') return 'Company Detail';
            if (parent === 'projects') return 'Project Detail';
            if (parent === 'clients') return 'Client Detail';
            if (parent === 'users') return 'User Detail';
            if (parent === 'tasks') return 'Task Detail';
            return 'Details';
        }

        return routeMapping[value] || value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, ' ');
    };

    const hiddenPaths = ['/tasks', '/clients', '/projects', '/tickets', '/project-templates', '/my-assignments', '/companies'];
    if (pathnames.length === 0 || hiddenPaths.includes(location.pathname)) {
        return null;
    }

    return (
        <Box sx={{ 
            mb: 2, 
            mt: 1,
            display: 'inline-flex',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(4px)',
            padding: '4px 12px',
            borderRadius: '16px',
            width: 'fit-content'
        }}>
            <MuiBreadcrumbs
                separator={<NavigateNext fontSize="small" />}
                aria-label="breadcrumb"
                sx={{
                    '& .MuiBreadcrumbs-li': {
                        display: 'flex',
                        alignItems: 'center',
                    }
                }}
            >
                <Link component={RouterLink} to={pathnames[0] === 'inventory' ? '/inventory' : '/'} underline="hover" color="inherit">
                    Dashboard
                </Link>
                {pathnames.map((value, index) => {
                    const last = index === pathnames.length - 1;
                    const to = `/${pathnames.slice(0, index + 1).join('/')}`;
                    const name = getBreadcrumbName(value, index, pathnames);

                    return last ? (
                        <Typography color="text.primary" key={to}>
                            {name}
                        </Typography>
                    ) : (
                        <Link component={RouterLink} to={to} underline="hover" color="inherit" key={to}>
                            {name}
                        </Link>
                    );
                })}
            </MuiBreadcrumbs>
        </Box>
    );
};

export default Breadcrumbs;
